import { type ReactNode, useMemo } from 'react';

import { toast } from '@surface/components';

import { useExportReadyRealtimeHandler } from '../hooks/use-export-ready-realtime-handler';
import { useKnockNotifications } from '../hooks/use-knock-notifications';
import { NotificationsFeedContext } from './notifications-feed-context';

type KnockFeedsProviderProps = {
  children: ReactNode;
};

export const KnockFeedsProvider = ({ children }: KnockFeedsProviderProps) => {
  const onExportReadyRealtime = useExportReadyRealtimeHandler();

  const notifications = useKnockNotifications({
    onRealtimeNotification: onExportReadyRealtime,
    onFetchError: (err) => {
      toast.error('Failed to load notifications', {
        description: err.message,
      });
    },
    onLoadMoreError: (err) => {
      toast.error('Failed to load more notifications', {
        description: err.message,
      });
    },
  });

  const notificationsValue = useMemo(
    () => ({
      isAvailable: true,
      items: notifications.items,
      unreadCount: notifications.unreadCount,
      isLoading: notifications.isLoading,
      filter: notifications.filter,
      setFilter: notifications.setFilter,
      reset: notifications.reset,
      markAsRead: notifications.markAsRead,
      markAllAsRead: notifications.markAllAsRead,
      markAsUnread: notifications.markAsUnread,
      markAsSeen: notifications.markAsSeen,
      archive: notifications.archive,
      hasNextPage: notifications.hasNextPage,
      loadMore: notifications.loadMore,
      isLoadingMore: notifications.isLoadingMore,
      prefetchAll: notifications.prefetchAll,
    }),
    [notifications]
  );

  return (
    <NotificationsFeedContext.Provider value={notificationsValue}>
      {children}
    </NotificationsFeedContext.Provider>
  );
};

/** @deprecated Use {@link KnockFeedsProvider}. */
export const NotificationsFeedProvider = KnockFeedsProvider;
